import React, { useState } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Eye, EyeOff, Loader2, ShieldCheck, ArrowLeft, MailCheck, KeyRound } from "lucide-react";
import { bgWithImageStyle } from "../utils/themeBg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/wingman/7b8b7474-e52e-4388-adc0-a2361520c0a4/attachments/8840390a845949e6b9092405676c1743_image.png";

const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen text-white flex flex-col" style={bgWithImageStyle()} data-testid="auth-page">
    <header className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
      <Link to="/" data-testid="auth-back-link" className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#94A3B8] hover:text-white">
        <ArrowLeft className="w-3.5 h-3.5" />
        Torna al sito
      </Link>
      <img src={LOGO_URL} alt="LEOVERA" className="h-10 w-auto bg-white rounded-md p-1" />
    </header>
    <main className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <span className="text-[10px] tracking-[0.36em] uppercase text-[#C0C0C0]">Area Membri</span>
          <h1 className="mt-3 font-[Outfit] text-white text-3xl tracking-tight font-light">{title}</h1>
          <p className="mt-2 text-[#94A3B8] text-sm">{subtitle}</p>
        </div>
        <div className="bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md p-7 shadow-2xl">
          {children}
        </div>
        <p className="mt-6 text-center text-[10px] tracking-[0.24em] uppercase text-[#94A3B8] inline-flex items-center justify-center gap-2 w-full">
          <ShieldCheck className="w-3 h-3 text-emerald-400" />
          Dati protetti · bcrypt + JWT
        </p>
      </div>
    </main>
  </div>
);

export const CustomerLogin = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const r = await login(email, password, rememberMe);
    setBusy(false);
    if (r.ok) nav(loc.state?.from || "/account", { replace: true });
    else setError(r.error);
  };

  return (
    <AuthShell title="Accedi" subtitle="Bentornato! Inserisci le tue credenziali per continuare.">
      <form onSubmit={onSubmit} data-testid="customer-login-form" className="space-y-5">
        <div>
          <label className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8]">Email</label>
          <input type="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)}
                 data-testid="customer-login-email"
                 className="mt-2 w-full bg-[var(--lv-bg,#060A14)] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C0C0C0]" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8]">Password</label>
          <div className="mt-2 relative">
            <input type={showPwd ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                   data-testid="customer-login-password"
                   className="w-full bg-[var(--lv-bg,#060A14)] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C0C0C0]" />
            <button type="button" onClick={() => setShowPwd((v) => !v)} aria-label="Mostra password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-white">
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {error && <div data-testid="customer-login-error" className="px-3 py-2 rounded-sm bg-red-500/10 border border-red-500/30 text-red-300 text-[12px]">{error}</div>}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              data-testid="customer-login-remember"
              className="w-4 h-4 accent-[#C0C0C0] cursor-pointer shrink-0"
            />
            <span className="text-[11px] text-[#94A3B8] group-hover:text-[#CBD5E1]">Resta collegato</span>
          </label>
          <Link to="/password-dimenticata" data-testid="link-forgot-password"
                className="text-[11px] text-[#94A3B8] hover:text-[#C0C0C0] underline underline-offset-2">
            Password dimenticata?
          </Link>
        </div>
        <button type="submit" disabled={busy} data-testid="customer-login-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#C0C0C0] text-[#060A14] py-3.5 rounded-sm text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-white disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accedi"}
        </button>
        <p className="text-center text-[11px] text-[#94A3B8]">
          Non hai un account? <Link to="/register" data-testid="link-register" className="text-[#C0C0C0] hover:text-white">Registrati</Link>
        </p>
      </form>
    </AuthShell>
  );
};

export const CustomerRegister = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [marketingAuthorized, setMarketingAuthorized] = useState(false);
  const [renatoOptin, setRenatoOptin] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!privacyAccepted) {
      setError("Devi accettare la Privacy Policy per creare l'account.");
      return;
    }
    setBusy(true);
    const r = await register(form.email, form.password, form.name, {
      privacy_accepted: privacyAccepted,
      marketing_authorized: marketingAuthorized,
      renato_whatsapp_optin: renatoOptin,
    });
    setBusy(false);
    if (r.ok) {
      if (renatoOptin) setRegistered(true);
      else nav("/account", { replace: true });
    } else setError(r.error);
  };

  if (registered) {
    return (
      <AuthShell title="Benvenuto in LEOVERA!" subtitle="Il tuo account è pronto.">
        <div className="text-center py-2" data-testid="register-success-renato">
          <p className="text-[#E5E4E2] text-sm leading-relaxed">
            ✅ Registrazione completata! Aggiungi Leo su WhatsApp per ricevere assistenza personalizzata:
          </p>
          <a href="https://wa.me/393277620114?text=Ciao%20Leo!%20Mi%20sono%20registrato%20su%20LEOVERA%20Nutrition"
             target="_blank" rel="noopener noreferrer" data-testid="renato-whatsapp-btn"
             className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white text-[13px] font-semibold shadow-lg transition-transform hover:scale-[1.03]"
             style={{ backgroundColor: "#25D366" }}>
            💬 Aggiungi Leo su WhatsApp
          </a>
          <div className="mt-6">
            <Link to="/account" data-testid="register-goto-account"
                  className="text-[11px] tracking-[0.24em] uppercase text-[#C0C0C0] hover:text-white underline underline-offset-4">
              Vai al mio account →
            </Link>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Crea il tuo account" subtitle="Inizia a guadagnare punti fedeltà ad ogni acquisto.">
      <form onSubmit={onSubmit} data-testid="customer-register-form" className="space-y-5">
        <div>
          <label className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8]">Nome completo</label>
          <input required value={form.name} onChange={set("name")} data-testid="customer-register-name"
                 placeholder="Mario Rossi"
                 className="mt-2 w-full bg-[var(--lv-bg,#060A14)] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C0C0C0]" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8]">Email</label>
          <input type="email" required value={form.email} onChange={set("email")} data-testid="customer-register-email"
                 placeholder="mario@email.it"
                 className="mt-2 w-full bg-[var(--lv-bg,#060A14)] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C0C0C0]" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8]">Password (min. 6 caratteri)</label>
          <div className="mt-2 relative">
            <input type={showPwd ? "text" : "password"} required minLength={6} value={form.password} onChange={set("password")}
                   data-testid="customer-register-password"
                   className="w-full bg-[var(--lv-bg,#060A14)] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C0C0C0]" />
            <button type="button" onClick={() => setShowPwd((v) => !v)} aria-label="Mostra password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-white">
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {error && <div data-testid="customer-register-error" className="px-3 py-2 rounded-sm bg-red-500/10 border border-red-500/30 text-red-300 text-[12px]">{error}</div>}

        {/* GDPR consent */}
        <div className="space-y-3 pt-2 border-t border-white/5">
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              required
              checked={privacyAccepted}
              onChange={(e) => setPrivacyAccepted(e.target.checked)}
              data-testid="register-privacy-checkbox"
              className="mt-0.5 w-4 h-4 accent-[#C0C0C0] cursor-pointer shrink-0"
            />
            <span className="text-[11px] leading-relaxed text-[#CBD5E1]">
              <span className="text-red-300 mr-1">*</span>
              Ho letto e accetto la{" "}
              <Link to="/privacy" target="_blank" className="text-[#C0C0C0] underline underline-offset-2 hover:text-white">
                Privacy Policy
              </Link>{" "}
              e i{" "}
              <Link to="/termini" target="_blank" className="text-[#C0C0C0] underline underline-offset-2 hover:text-white">
                Termini e Condizioni
              </Link>{" "}
              e acconsento al trattamento dei miei dati personali per la gestione dell'account e degli ordini.
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={marketingAuthorized}
              onChange={(e) => setMarketingAuthorized(e.target.checked)}
              data-testid="register-marketing-checkbox"
              className="mt-0.5 w-4 h-4 accent-[#C0C0C0] cursor-pointer shrink-0"
            />
            <span className="text-[11px] leading-relaxed text-[#94A3B8]">
              <span className="text-emerald-300/70 mr-1 text-[9px] tracking-[0.24em] uppercase">Opzionale</span>
              Autorizzo l'invio di comunicazioni marketing, newsletter e codici sconto personalizzati. Posso revocare il consenso in qualsiasi momento.
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={renatoOptin}
              onChange={(e) => setRenatoOptin(e.target.checked)}
              data-testid="register-renato-checkbox"
              className="mt-0.5 w-4 h-4 accent-[#C0C0C0] cursor-pointer shrink-0"
            />
            <span className="text-[11px] leading-relaxed text-[#94A3B8]">
              <span className="text-emerald-300/70 mr-1 text-[9px] tracking-[0.24em] uppercase">Opzionale</span>
              Desidero aggiungere Leo (Assistente AI) su WhatsApp per ricevere supporto e offerte esclusive
            </span>
          </label>
        </div>

        <button type="submit" disabled={busy} data-testid="customer-register-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#C0C0C0] text-[#060A14] py-3.5 rounded-sm text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-white disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crea Account"}
        </button>
        <p className="text-center text-[11px] text-[#94A3B8]">
          Hai già un account? <Link to="/login" data-testid="link-login" className="text-[#C0C0C0] hover:text-white">Accedi</Link>
        </p>
      </form>
    </AuthShell>
  );
};

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore. Riprova tra qualche minuto.");
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Password dimenticata" subtitle="Inserisci la tua email: ti invieremo il link per reimpostarla.">
      {sent ? (
        <div className="text-center py-4" data-testid="forgot-password-sent">
          <MailCheck className="w-10 h-10 text-emerald-400 mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-[#E5E4E2] text-sm">Se l'email è registrata, riceverai il link di reset a breve.</p>
          <p className="text-[#94A3B8] text-[11px] mt-2">Controlla anche la cartella spam.</p>
          <Link to="/login" className="mt-6 inline-block text-[11px] tracking-[0.24em] uppercase text-[#C0C0C0] hover:text-white">Torna al login</Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} data-testid="forgot-password-form" className="space-y-5">
          <div>
            <label className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8]">Email</label>
            <input type="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)}
                   data-testid="forgot-password-email"
                   className="mt-2 w-full bg-[var(--lv-bg,#060A14)] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C0C0C0]" />
          </div>
          {error && <div data-testid="forgot-password-error" className="px-3 py-2 rounded-sm bg-red-500/10 border border-red-500/30 text-red-300 text-[12px]">{String(error)}</div>}
          <button type="submit" disabled={busy} data-testid="forgot-password-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#C0C0C0] text-[#060A14] py-3.5 rounded-sm text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-white disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia link di reset"}
          </button>
          <p className="text-center text-[11px] text-[#94A3B8]">
            <Link to="/login" className="text-[#C0C0C0] hover:text-white">Torna al login</Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
};

export const ResetPassword = () => {
  const { token } = useParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Token non valido o scaduto.");
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Nuova password" subtitle="Scegli una nuova password per il tuo account.">
      {done ? (
        <div className="text-center py-4" data-testid="reset-password-done">
          <KeyRound className="w-10 h-10 text-emerald-400 mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-[#E5E4E2] text-sm">Password aggiornata! Ti stiamo portando al login…</p>
        </div>
      ) : (
        <form onSubmit={onSubmit} data-testid="reset-password-form" className="space-y-5">
          <div>
            <label className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8]">Nuova password (min. 6 caratteri)</label>
            <div className="mt-2 relative">
              <input type={showPwd ? "text" : "password"} required minLength={6} autoFocus
                     value={password} onChange={(e) => setPassword(e.target.value)}
                     data-testid="reset-password-input"
                     className="w-full bg-[var(--lv-bg,#060A14)] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C0C0C0]" />
              <button type="button" onClick={() => setShowPwd((v) => !v)} aria-label="Mostra password"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-white">
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {error && <div data-testid="reset-password-error" className="px-3 py-2 rounded-sm bg-red-500/10 border border-red-500/30 text-red-300 text-[12px]">{String(error)}</div>}
          <button type="submit" disabled={busy} data-testid="reset-password-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#C0C0C0] text-[#060A14] py-3.5 rounded-sm text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-white disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Imposta nuova password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
};
